import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react'
import { useParams } from 'react-router-dom'
import B2BLayout from '../../../layouts/b2b'
import { Result, Skeleton, Tag } from 'antd'
import { ResultStatusType } from 'antd/lib/result'
import fetchAPI, { IHttpError } from '../../../../lib/utils/fetch-api'
import { getEmployeeFormData } from './selectors'
import { EmployeeFormData } from './types/types'
import { EmployeeFormHeader } from './ui/forms/form-header'
import { EmployeeCardContent } from './ui/employee-card-content'
import DefaultJsonFormContext from '../../../components/v2/default-json-form/context/default-json-form-context'
import { DefaultJsonFormContextProps } from '../../../components/v2/default-json-form/context/default-json-form-context-props'
import { EmployeeComplianceProvider } from './context/employee-compliance-context'
import { useAppDispatch } from '../../../hooks/useAppDispatch/useAppDispatch'
import { setTimelineClear } from '../../../store/timeline/actions'
import { getTimeLineData } from '../../../store/timeline/action-creators'
import { useUnsavedChangesNavigationGuard } from './hooks/use-unsaved-changes-navigation-guard'

const EmployeeCardPage = () => {
  const { id } = useParams<{ id: string }>()
  const dispatch = useAppDispatch()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<any>(null)
  const [formData, setFormData] = useState<EmployeeFormData | null>(null)
  const [enums, setEnums] = useState<Record<string, any[]>>({})
  const [isEditMode, setIsEditMode] = useState<boolean>(false)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const hasUnsavedChangesRef = useRef(false)

  const handleDirtyChange = useCallback((isDirty: boolean) => {
    hasUnsavedChangesRef.current = isDirty
    setHasUnsavedChanges(isDirty)
  }, [])

  const clearUnsavedChanges = useCallback(() => {
    hasUnsavedChangesRef.current = false
    setHasUnsavedChanges(false)
  }, [])

  useUnsavedChangesNavigationGuard({
    enabled: isEditMode,
    hasUnsavedChanges,
    hasUnsavedChangesRef,
    onDiscard: clearUnsavedChanges,
  })

  const statusTag = useMemo(() => {
    const employee = formData?.employee
    if (!employee || id === 'new') return null
    const status = employee.status ?? (employee.is_active !== false ? 'active' : 'dismissed')
    if (status === 'maternity') return <Tag color="orange">Декрет</Tag>
    if (status === 'dismissed') return <Tag color="red">Уволен</Tag>
    return <Tag color="green">Активен</Tag>
  }, [formData?.employee, id])

  const pageTitle = useMemo(() => {
    if (formData?.employee) {
      return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <span>{`Сотрудник: ${formData.employee.fio}`}</span>
          {statusTag}
        </span>
      )
    }
    return `Сотрудник #${id}`
  }, [id, formData?.employee, statusTag])

  const breadcrumbTitle = useMemo(
    () => (formData?.employee ? `Сотрудник: ${formData.employee.fio}` : `Сотрудник #${id}`),
    [id, formData?.employee],
  )

  const breadcrumbs = useMemo(
    () => [
      {
        href: '/hr-employees',
        title: 'Сотрудники',
      },
      {
        title: breadcrumbTitle,
      },
    ],
    [breadcrumbTitle],
  )

  const entityType = 'hr_employee'

  const defaultProps = useMemo(
    (): DefaultJsonFormContextProps => ({
      entityId: id,
      entityType: entityType,
    }),
    [id],
  )

  const loadData = useCallback(async () => {
    if (!id) return

    try {
      setLoading(true)
      setError(null)
      setFormData(null)

      // API принимает только \d+|new — иначе Express отдаёт не-JSON 404
      if (id !== 'new' && !/^\d+$/.test(id)) {
        setError({ message: 'Сотрудник не найден', statusCode: 404 })
        return
      }

      const response = await fetchAPI(`/api/hr/employees/${id}`)

      if (response?.errors || response?.error) {
        const raw = response.errors || response.error
        const message = Array.isArray(raw) ? raw.map(String).join(', ') : String(raw)
        setError({ message: message || 'Сотрудник не найден', statusCode: 404 })
        return
      }

      if (response) {
        const processedData = getEmployeeFormData(response)

        if (!processedData) {
          setError({
            message: 'Не удалось обработать данные карточки сотрудника',
            statusCode: 'error',
          })
          return
        }

        setFormData(processedData)

        if (response.enums) {
          setEnums(response.enums)
        }
      }
    } catch (err: any) {
      console.error('Ошибка при загрузке данных сотрудника:', err)
      const statusCode = Number(err?.statusCode) || 'error'
      setError({
        message:
          err?.message ||
          (statusCode === 404 ? 'Сотрудник не найден' : 'При загрузке карточки возникла ошибка'),
        statusCode: statusCode === 404 ? 404 : statusCode,
      })
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => {
    loadData()
  }, [loadData])

  const handleRefresh = useCallback(() => {
    loadData()
    setIsEditMode(false)
    clearUnsavedChanges()
    // Обновляем ленту (события увольнения, переприёма, перевода и т.д.)
    if (id && id !== 'new') {
      dispatch(setTimelineClear())
      dispatch(getTimeLineData('hr_employee', id))
    }
  }, [loadData, id, dispatch, clearUnsavedChanges])

  const handleEditModeChange = useCallback(
    (editMode: boolean) => {
      if (!editMode) {
        clearUnsavedChanges()
      }
      setIsEditMode(editMode)
    },
    [clearUnsavedChanges],
  )

  const saveFormRef = useRef<(() => Promise<void>) | null>(null)

  const handleSave = useCallback(async () => {
    if (saveFormRef.current) {
      try {
        await saveFormRef.current()
        handleRefresh()
      } catch (error) {
        console.error('Ошибка при сохранении:', error)
      }
    }
  }, [handleRefresh])

  const complianceContextValue = useMemo(() => {
    if (!id || id === 'new' || !formData) return null
    const numericId = Number(id)
    if (!numericId) return null
    return {
      employeeId: numericId,
      compliance: formData.compliance ?? null,
      employeeStatus: formData.employee?.status,
      onRefresh: loadData,
    }
  }, [id, formData, loadData])

  if (!id) return null

  const isNotFound =
    typeof error === 'object' && error != null && Number(error.statusCode) === 404

  return (
    <B2BLayout breadcrumbs={breadcrumbs} pageTitle={pageTitle}>
      {loading && <Skeleton active />}
      {!loading && error && (
        <Result
          status={isNotFound ? 404 : (((error as IHttpError)?.statusCode || 'error') as ResultStatusType)}
          title={isNotFound ? 'Не найдено' : 'Ошибка'}
          subTitle={
            typeof error === 'object'
              ? (error as IHttpError)?.message || (isNotFound ? 'Сотрудник не найден' : 'Ошибка')
              : String(error)
          }
        />
      )}
      {!loading && !error && formData && (
        <DefaultJsonFormContext.Provider value={defaultProps}>
          <EmployeeComplianceProvider value={complianceContextValue}>
            <EmployeeFormHeader
              id={id}
              employee={formData.employee || null}
              enums={enums}
              isEditMode={isEditMode}
              hasUnsavedChanges={hasUnsavedChanges}
              onRefresh={handleRefresh}
              onEditModeChange={handleEditModeChange}
              onSave={handleSave}
            />
            <EmployeeCardContent
              id={id}
              formData={formData}
              loading={loading}
              isEditMode={isEditMode}
              onSaveRef={saveFormRef}
              onDirtyChange={handleDirtyChange}
            />
          </EmployeeComplianceProvider>
        </DefaultJsonFormContext.Provider>
      )}
    </B2BLayout>
  )
}

export default EmployeeCardPage
